import { Autocomplete, Box, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Skeleton, TextField, Typography, useTheme } from "@mui/material";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useGetBreakdownChartDataQuery, useGetFiltersQuery } from "src/context/api/conversionsAnalysis/api";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { setFilterConversionType, setFilterLoadedCountry, setFilterLoadedDevice, setFilterLoadedPlatform, setFilterMinDatePicker } from "src/context/reducers/filter-settings/filterSlice";
import ChartDonutConversions from "src/sections/charts/chart-donut-conversions";
import ChartAreaSpline from "src/sections/charts/chart-line-conversions";
import _ from "lodash";
import Iconify from "src/components/iconify";
import { fDate } from "src/utils/format-time";

export default function ConversionsAnalysis() {
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    const dispatch = useDispatch();
    const theme = useTheme();
    const [metric, setMetric]: any = useState("Revenue");
    const [dimension, setDimension]: any = useState("channel");
    const [conversionTypeOptions, setConversionTypeOptions] = useState<string[]>([]);
    const [conversionType, setConversionType] = useState<string>('');


    const MetricSelector = ({ workspaceTemplate, value, onChange }: any) => {
        const isEcommerce = workspaceTemplate === "E-commerce";

        useEffect(() => {
            if (!isEcommerce && value !== "Conversions") {
                onChange("Conversions");
            }
        }, [workspaceTemplate]);

        if (!isEcommerce) return null;

        return (
            <FormControl size="small" sx={{ minWidth: 160, mr: 2 }}>
                <InputLabel id="metric-label">Metric</InputLabel>
                <Select
                    labelId="metric-label"
                    id="metric-select"
                    value={value}
                    label="Metric"
                    onChange={(e) => onChange(e.target.value)}
                >
                    <MenuItem value="Revenue">Revenue</MenuItem>
                    <MenuItem value="Conversions">Conversions</MenuItem>
                </Select>
            </FormControl>
        );
    }

    const DIMENSION_OPTIONS = [
        { label: "By Channel", value: "channel" },
        { label: "By Country", value: "country" },
        { label: "By Device", value: "device" },
        { label: "By Platform", value: "platform" },
    ].filter(option => {
        if (option.value === "country") {
            return !!(filterStore?.loadedCountries && filterStore.loadedCountries.length > 1);
        }
        if (option.value === "platform") {
            return !!(filterStore?.loadedPlatform && filterStore.loadedPlatform.length > 1);
        }
        return true;
    });

    const { data: filtersData } = useGetFiltersQuery({
        workspaceId: workspace?.id,
        start_date: filterStore?.start,
        end_date: filterStore?.end,
        countries: filterStore?.countries,
        devices: filterStore?.devices,
        platforms: filterStore?.platforms,
    }, { skip: !workspace?.id });

    useEffect(() => {
        if (filtersData) {
            dispatch(setFilterLoadedCountry(filtersData.countries || []));
            dispatch(setFilterLoadedDevice(filtersData.devices || []));
            dispatch(setFilterLoadedPlatform(filtersData.platforms || []));
            dispatch(setFilterMinDatePicker(filtersData.min_date || null));
        }
    }, [filtersData, dispatch]);

    const { data: breakdownData, isLoading: isLoadingBreakdown, isFetching: isFetchingBreakdown, error: breakdownError } = useGetBreakdownChartDataQuery({
        workspaceId: workspace?.id,
        start_date: filterStore?.start,
        end_date: filterStore?.end,
        conversionType: filterStore?.conversionType,
        metric: metric,
        dimension: dimension,
        timestep: filterStore?.timestep,
        devices: filterStore.devices,
        countries: filterStore.countries,
        platform: filterStore?.platforms,
    }, {
        skip: !workspace?.id
    });

    const rawAreaData: any = breakdownData?.area || [];

    const categories: any = Array.from(
        new Set(rawAreaData.map((d: any) => d.date))
    );

    const allDates = rawAreaData.map((d: any) => {
        return fDate(d?.date, 'dd/MM/yyyy');
    });

    const uniqueDate = Array.from(new Set(allDates));

    const grouped = _.groupBy(rawAreaData, "label");

    const series = Object.entries(grouped).map(([label, values]: [string, any]) => {
        const dataPerDate = _.keyBy(values, "date");
        return {
            name: label,
            data: categories.map((date: any) => dataPerDate[date]?.value || 0)
        };
    });

    useEffect(() => {
        if (workspace?.template === "Lead") {
            setMetric("Conversions");
        }
    }, [workspace])

    useEffect(() => {
        if (!workspace) return;

        let options: string[] = [];
        let defaultOption = '';

        if (workspace.custom === true) {
            options = ["Brand (Custom source)", "Brand (Ad-based)"];
            defaultOption = "Brand (Custom source)";
        } else if (workspace.conversions_google_analytics && workspace.conversions_google_analytics.length > 0) {
            options = ["Total", "Brand (Tracking-based)", "Brand (Ad-based)"];
            defaultOption = "Total";
        } else {
            options = ["Brand (Ad-based)"];
            defaultOption = "Brand (Ad-based)";
        }

        setConversionTypeOptions(options);
        setConversionType(defaultOption);
        dispatch(setFilterConversionType(defaultOption));
    }, [workspace]);

    return (
        <Paper sx={{
            backgroundColor: theme.palette.mode === "dark" ? '#212b36' : 'white',
            boxShadow: '0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
            height: '530px!important',
            borderRadius: 2,
            marginTop: 2,
        }}>
            {isFetchingBreakdown || (!breakdownData && !breakdownError)
                ? <Skeleton width={'100%'} height={'100%'} style={{ borderRadius: 10 }} variant="rectangular" animation={"wave"}>
                    <>
                        <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                        <Typography color={"success.main"}>Active</Typography>
                    </>
                </Skeleton>
                :
                <>
                    <div style={{ paddingLeft: 25, paddingRight: 25 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ marginBottom: 20, paddingTop: 20 }}>
                                <Typography variant="overline" color="textSecondary" sx={{ mb: 1 }}>Conversions</Typography>
                                <Typography variant="h6" sx={{ mb: 5 }}>
                                    {filterStore?.conversionType} Search {metric} By {dimension.charAt(0).toUpperCase() + dimension.slice(1)}
                                </Typography>
                            </div>

                            <Box sx={{display: 'flex', flexDirection: 'row'}}>
                                <Box>
                                    <Autocomplete
                                        size="small"
                                        sx={{ marginRight: 2, width: 200 }}
                                        disableClearable
                                        options={conversionTypeOptions}
                                        value={conversionType}
                                        onChange={(event, newValue) => {
                                            setConversionType(newValue);
                                            dispatch(setFilterConversionType(newValue));
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Conversion Type" />
                                        )}
                                    />
                                </Box>

                                {workspace?.template === "E-commerce" &&
                                    <MetricSelector
                                        workspaceTemplate={workspace?.template}
                                        value={metric}
                                        onChange={setMetric}
                                    />
                                }

                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <InputLabel id="dimension-label">Breakdown</InputLabel>
                                    <Select
                                        labelId="dimension-label"
                                        id="dimension-select"
                                        value={dimension}
                                        label="Breakdown"
                                        onChange={(e) => setDimension(e.target.value)}
                                    >
                                        {DIMENSION_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                            </Box>
                        </div>
                    </div>

                    <Grid container>
                        <Grid item xs={4}>
                            <Box>
                                <ChartDonutConversions
                                    series={breakdownData?.donut?.map((item: any) => item.value) || []}
                                    labels={breakdownData?.donut?.map((item: any) => item.label) || []}
                                    breakdownDimension={dimension}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={8} px={10}>
                            <Box>
                                <ChartAreaSpline
                                    series={series}
                                    categories={uniqueDate}
                                    metrics={metric}
                                    breakdownDimension={dimension}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </>
            }
        </Paper>
    );
}